export const realEstateAgentsData = {
  industry: "Real Estate Agents",
  slug: "real-estate-agents",
  
  seo: {
    title: "Intake Forms for Real Estate Agents | HeyIntake",
    description: "Qualify buyers and sellers faster with AI-powered intake forms. Capture budget, timeline, property preferences, and pre-qualification status before your first showing.",
    keywords: [
      "real estate intake form",
      "buyer questionnaire",
      "seller intake form",
      "real estate lead qualification",
      "property inquiry form",
      "realtor client intake"
    ]
  },

  heroTitle: "Intake Forms for Real Estate Agents",
  heroSubtitle: "Qualify buyers and sellers before the first showing. Know their budget, timeline, and must-haves upfront.",
  heroDescription: "Stop wasting weekends on unqualified showings. HeyIntake helps you capture pre-qualification status, property preferences, and motivation level so you can focus on clients ready to close.",
  
  whyAIScoring: {
    title: "Why Real Estate Agents Need Smart Intake",
    intro: "Every agent knows the pain: driving across town for a showing, only to discover the buyer isn't pre-approved or the seller isn't serious about their timeline.",
    paragraphs: [
      "Traditional lead capture — Zillow inquiries, open house sign-ins, website contact forms — gives you a name and maybe a phone number. That's it. You're left playing phone tag trying to figure out if someone is a serious buyer with financing in place or just casually browsing.",
      "HeyIntake changes the game. Before you book a single showing, you know their pre-approval status, budget range, must-have features, timeline to move, and motivation level. Our AI scores each lead so you can prioritize the hot buyers ready to write offers over the tire-kickers.",
      "For agents handling multiple buyers and sellers, this means fewer wasted showings, faster path to commission, and more time for the clients who are actually ready to transact."
    ]
  },

  painPoints: [
    {
      title: "Unqualified Showings",
      description: "Spending weekends showing homes to buyers who aren't pre-approved or can't afford the properties they're viewing."
    },
    {
      title: "Unknown Motivation Level",
      description: "No way to tell if a lead is ready to buy this month or just casually browsing for 'someday.'"
    },
    {
      title: "Missing Key Preferences",
      description: "Learning mid-showing that they need a home office, guest suite, or specific school district you didn't know about."
    },
    {
      title: "Seller Timeline Surprises",
      description: "Discovering a potential listing isn't serious about selling or has an unrealistic price expectation."
    },
    {
      title: "Lead Source Chaos",
      description: "Zillow leads, open house contacts, and referrals all mixed together with no qualification system."
    },
    {
      title: "Slow Follow-Up",
      description: "Hot leads go cold while you're busy with showings and paperwork, losing deals to faster agents."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Basics",
      fields: [
        "Full name",
        "Phone number",
        "Email address",
        "Are you buying, selling, or both?",
        "How did you hear about me?"
      ]
    },
    {
      category: "Buyer Qualification",
      fields: [
        "Pre-approval status (yes/no/in progress)",
        "Lender name (if pre-approved)",
        "Budget range",
        "Down payment amount",
        "Timeline to purchase"
      ]
    },
    {
      category: "Property Preferences",
      fields: [
        "Desired location/neighborhoods",
        "Property type (single family, condo, townhouse)",
        "Minimum bedrooms and bathrooms",
        "Must-have features (garage, yard, home office)",
        "Deal-breakers"
      ]
    },
    {
      category: "Seller Information",
      fields: [
        "Property address",
        "Reason for selling",
        "Desired timeline",
        "Price expectation",
        "Current mortgage balance (optional)"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Real Estate Intake Form",
    description: "Here's what a high-converting buyer intake form looks like:",
    fields: [
      { label: "Are you pre-approved for a mortgage?", type: "select", options: ["Yes", "No", "In progress"] },
      { label: "What's your budget range?", type: "select", options: ["$200K-$300K", "$300K-$400K", "$400K-$500K", "$500K+"] },
      { label: "Preferred location/neighborhood", type: "text" },
      { label: "Property type", type: "select", options: ["Single Family", "Condo", "Townhouse", "Multi-family"] },
      { label: "Minimum bedrooms", type: "select", options: ["1", "2", "3", "4+"] },
      { label: "Timeline to purchase", type: "select", options: ["ASAP", "1-3 months", "3-6 months", "6+ months"] },
      { label: "Must-have features", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Real Estate",
    subtitle: "From lead to qualified client in three steps",
    steps: [
      {
        title: "Capture Complete Information",
        description: "Leads fill out your intake form with budget, pre-approval status, preferences, and timeline."
      },
      {
        title: "AI Qualifies & Prioritizes",
        description: "Our AI scores each lead based on readiness to transact, budget fit, and motivation level."
      },
      {
        title: "Focus on Ready Buyers",
        description: "Spend your time on pre-approved buyers ready to make offers, not tire-kickers."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Lead Qualification for Agents",
    description: "HeyIntake's AI analyzes each inquiry to identify serious buyers and sellers, so you know who deserves your immediate attention.",
    factors: [
      "Pre-approval status and lender verification",
      "Budget alignment with your target market",
      "Timeline urgency (ASAP vs. 6+ months)",
      "Completeness of property preferences",
      "Motivation indicators in their responses"
    ],
    exampleLead: {
      name: "Jennifer & Mark Thompson",
      context: "Pre-Approved • $450K Budget • Moving in 60 days",
      summary: "Pre-approved with Chase for $475K. Relocating for work, must close within 60 days. Looking for 3BR in Westside school district. High urgency, serious buyers.",
      score: 96
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Pre-Qualify Before Showings",
      description: "Know their budget, pre-approval status, and timeline before you book a single showing."
    },
    {
      icon: "clock",
      title: "Prioritize Hot Leads",
      description: "AI identifies buyers ready to write offers so you can respond within minutes, not hours."
    },
    {
      icon: "users",
      title: "Segment Buyers & Sellers",
      description: "Automatically route buyer and seller inquiries to different follow-up sequences."
    },
    {
      icon: "file",
      title: "Capture Listing Details",
      description: "Sellers provide property details, photos, and timeline before your listing appointment."
    },
    {
      icon: "zap",
      title: "Automate Open House Follow-Up",
      description: "Convert sign-in sheets to qualified leads with automated intake forms."
    },
    {
      icon: "chart",
      title: "Track Your Pipeline",
      description: "See all leads in one dashboard with status tracking from inquiry to closing."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" },
    { name: "Intake Forms for Lawyers", path: "/intake-form-for-lawyers" },
    { name: "Intake Forms for Accountants", path: "/intake-form-for-accountants" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" }
  ],

  ctaText: "Ready to Close More Deals?"
};
